'use client';

import { useCallback, useLayoutEffect, useRef } from 'react';
import type { BrainTypingRequest } from '../../lib/types';

interface TypingOptions {
  session: string | null;
  client: string | null;
  generation: number | null;
  ready: boolean;
  shared: boolean;
  visible: boolean;
  send: (request: BrainTypingRequest) => Promise<unknown>;
}

export function useConversationTyping({ session, client, generation, ready, shared, visible, send }: TypingOptions) {
  const sendRef = useRef(send);
  sendRef.current = send;
  const binding = useRef({ session, client, generation, sequence: 0 });
  const active = useRef(false);
  const lastInput = useRef(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const cycle = useRef(0);
  const enabled = useRef(false);
  enabled.current = ready && shared && visible;
  const emit = useCallback((typing: boolean) => {
    const b = binding.current;
    if (b.session === null || b.client === null || b.generation === null) return;
    const request: BrainTypingRequest = { session: b.session, client: b.client, generation: b.generation, sequence: ++b.sequence, typing };
    const issuedCycle = cycle.current;
    void sendRef.current(request).catch(() => {
      console.error('Typing presence request failed');
      if (typing && cycle.current === issuedCycle) {
        if (timer.current !== null) clearInterval(timer.current);
        timer.current = null;
        active.current = false;
        cycle.current += 1;
      }
    });
  }, []);
  const stop = useCallback(() => {
    cycle.current += 1;
    if (timer.current !== null) clearInterval(timer.current);
    timer.current = null;
    if (active.current) { active.current = false; emit(false); }
  }, [emit]);
  useLayoutEffect(() => {
    if (binding.current.session !== session || binding.current.client !== client || binding.current.generation !== generation) {
      stop();
      binding.current = { session, client, generation, sequence: 0 };
    }
    if (!ready || !shared || !visible) stop();
    return stop;
  }, [session, client, generation, ready, shared, visible, stop]);
  const onDraftInput = useCallback((hasText: boolean) => {
    const current = binding.current;
    if (current.session !== session || current.client !== client || current.generation !== generation) return;
    if (!hasText) { stop(); return; }
    if (!enabled.current || !ready || !shared || !visible || session === null || client === null || generation === null) return;
    lastInput.current = Date.now();
    if (timer.current !== null) return;
    active.current = true;
    cycle.current += 1;
    emit(true);
    timer.current = setInterval(() => {
      if (Date.now() - lastInput.current < 2000) {
        cycle.current += 1;
        emit(true);
      }
      else {
        // Let the server's five-second TTL expire, but retain the ability to send an explicit stop.
        if (timer.current !== null) clearInterval(timer.current);
        timer.current = null;
      }
    }, 2000);
  }, [ready, shared, visible, session, client, generation, emit, stop]);
  const onStop = useCallback(() => {
    const current = binding.current;
    if (current.session === session && current.client === client && current.generation === generation) stop();
  }, [session, client, generation, stop]);
  return { onDraftInput, onBlur: onStop, onSend: onStop };
}
